import { useLocation, useNavigate } from "react-router-dom";
import { Briefcase, CalendarClock, ListChecks, Plus, Sparkles, Wrench } from "lucide-react";
import { useUserInfo } from "../../context/user";
import { useUsersContext } from "../../context/usersContext";
import { useTaskDrafts } from "../../queries/taskDrafts";
import { Button } from "components/ui/button";
import { cn } from "lib/utils";
import { buildAuthedPath } from "lib/buildAuthedPath";

/**
 * Primary-nav zone of the unified app shell left sidebar (Lovable AppSidebar
 * zone-1). A vertical list of the six core destinations, each a ghost-button
 * row with a lucide icon + label (and an optional live count badge).
 *
 * Routes use the same encoded authed deep-link pattern as the now-removed legacy Navbar:
 * when both the active user email and active location id are valid, the path is
 * prefixed with `btoa("<email>:<locationId>")`; otherwise the bare `/app/...`
 * path is used. Active-route highlight is driven by useLocation().
 */
export default function AppNav({ collapsed = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const userInfo = useUserInfo();
  const user = userInfo[0];
  const activeLocation = userInfo[4];
  const isValidEmail = userInfo[6];
  const isValidLocationId = userInfo[7];

  const { tasksData } = useUsersContext() || {};

  const tasksCount = (tasksData?.prompt || []).filter((t) => {
    const type = String(t?.type);
    return type !== "SuperAdmin" && type !== "super-admin";
  }).length;

  const { data: drafts = [] } = useTaskDrafts(activeLocation?.id);
  const draftsCount = drafts.length;

  // Build the encoded authed deep-link via the shared helper. Falls back to
  // the bare path when the email/location are missing or fail validation.
  const buildPath = (path) =>
    buildAuthedPath(path, {
      email: user?.email,
      locationId: activeLocation?.id,
      isValidEmail,
      isValidLocationId,
    });

  const destinations = [
    // New Task lands on the chat home (same target as the brand logo), matching
    // the Lovable shell where New Task clears the chat and returns to the
    // landing. The create/schedule form lives under "Automate Tasks" below.
    { label: "New Task", path: "/app/chat", Icon: Plus },
    {
      label: "Your Tasks",
      path: "/app/chat",
      // Lands on the chat home and expands its Task Library (the standalone
      // All-Tasks page was merged into the landing under this anchor).
      hash: "#task-library",
      Icon: Briefcase,
      count: tasksCount,
    },
    {
      label: "Automate Tasks",
      path: "/app/chat/tasks/new",
      Icon: CalendarClock,
    },
    {
      label: "Drafts",
      path: "/app/chat/drafts",
      Icon: ListChecks,
      count: draftsCount,
    },
    {
      label: "Your Tools",
      path: "/app/settings",
      Icon: Wrench,
      state: { tab: "Integrations" },
    },
    { label: "Agents", path: "/app/ai-agents", Icon: Sparkles },
  ];

  // New Task and Your Tasks share the /app/chat path and are told apart by the
  // #task-library hash (Your Tasks anchors to the library; New Task does not).
  const isActive = ({ path, hash }) => {
    if (!location.pathname.endsWith(path)) return false;
    if (hash) return location.hash === hash;
    if (path === "/app/chat") return location.hash !== "#task-library";
    return true;
  };

  return (
    <nav aria-label="Primary" className="flex flex-col gap-1">
      {destinations.map(({ label, path, hash, Icon, count, state }) => {
        const active = isActive({ path, hash });
        const target = `${buildPath(path)}${hash || ""}`;
        return (
          <Button
            key={label}
            type="button"
            variant="ghost"
            onClick={() =>
              state ? navigate(target, { state }) : navigate(target)
            }
            aria-current={active ? "page" : undefined}
            aria-label={label}
            title={collapsed ? label : undefined}
            className={cn(
              "h-9 text-sm font-normal text-muted-foreground border-0",
              collapsed
                ? "w-9 mx-auto justify-center px-0"
                : "w-full justify-start gap-2 px-3",
              active
                ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
            )}
          >
            <Icon size={16} aria-hidden="true" />
            {!collapsed && <span className="flex-1 text-left">{label}</span>}
            {!collapsed && count > 0 && (
              <span
                aria-label={`${count} ${label}`}
                className="ml-auto text-xs text-muted-foreground/80"
              >
                ({count})
              </span>
            )}
          </Button>
        );
      })}
    </nav>
  );
}
